import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useTheme,
  useMediaQuery,
  ButtonGroup,
  Button,
  IconButton,
  MenuItem,
  TextField,
  CircularProgress, Box, Tooltip, Checkbox, Typography
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { rankItem } from '@tanstack/match-sorter-utils'
import { toast } from 'react-toastify'

import CustomTextField from '../atoms/CustomTextField'
import DebouncedInput from '../atoms/DebouncedInput'
import AdditionalEmailModal from '../molecules/AdditionalEmailModal'
import UploadExcelFirmListModalBtn
  from '../molecules/UploadExcelFirmListModalBtn'
import FirmListServerSide from './FirmListServerSide'
import AutoCompleteChip from '../atoms/AutoCompleteChip'
import McPageHelpers from '../../../../../pages/mc-components/McPageHelpers'
import { getUrlParams } from '../../../services/urlServices'
import { useGlobalStoreData } from '../../../../../store/globalStore'
import { getRegistryFirms } from '../../../services/B2BServices'
import FirmListSemiClientSide from './FirmListSemiClientSide'
import B2BAPI from '../../../services/B2BAPI'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import useFirmSelectionStore, { useFirmSelectionState, useFirmSelectionActions } from '../../../../../store/firmSelectionStore'
import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

let apiController

const FirmListManager = (props) => {
  const {
    firmState,
    sectorState,
    paginationState,
    emailState,
    uiConfig = {
      hideTabs: false,
      hideActionButtons: false,
      disableInteraction: false,
      hideWebsiteColumn: false
    },
    isServerSide,
    onRefresh
  } = props

  const {
    filteredRegistryFirmsData,
    setFilteredRegistryFirmsData,
    firmKeys,
    activeFirmKey,
    setActiveFirmKey,
    data,
    rowSelection,
    setRowSelection,
    setSelectedFirms,
    fetching,
    excelFirms,
    setExcelFirms
  } = firmState

  const {
    filteredSectors,
    setFilteredSectorsByKey,
    setSelectedTradeRegistrySectors,
    tradeRegistrySectors
  } = sectorState

  const { serverSidePagination, setServerSidePagination } = paginationState
  const { additionalEmails, setAdditionalEmails } = emailState

  const { t } = useTranslation()
  const theme = useTheme()
  const languageGSD = useGlobalStoreData.language()
  const queryParams = getUrlParams()
  const isBelowMdScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isBelowSmScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [uiState, setUiState] = useState({
    globalFilter: '',
    additionalEmailsModalOpen: false,
    pageSize: 10,
    registryFirmsFetching: false,
    clientFirmsFetching: false,
    searchKeywordByKey: {},
    importerCountriesByKey: {},
    exporterCountriesByKey: {}
  })

  const updateUiState = (updates) => setUiState(
    prev => ({ ...prev, ...updates }))

  const [clientData, setClientData] = useState(data)

  const filteredSectorsByKey = filteredSectors?.[activeFirmKey] || []

  const currentSearchKeyword = uiState.searchKeywordByKey[activeFirmKey] || ''
  const currentImporterCountries = uiState.importerCountriesByKey[activeFirmKey] || []
  const currentExporterCountries = uiState.exporterCountriesByKey[activeFirmKey] || []

  const countryData = useMemo(() => {
    const countries = languageGSD === 'tr' ? countryTR : countryEN
    return countries.sort((a, b) => a.countryName.localeCompare(b.countryName))
  }, [languageGSD])

  const setFilteredRegistryFirmsDataByKey = (key, val) => {
    const currentData = typeof filteredRegistryFirmsData === 'function'
      ? {}
      : (filteredRegistryFirmsData || {})
    setFilteredRegistryFirmsData({ ...currentData, [key]: val })
  }

  const setServerSidePaginationByKey = (key, val) => {
    setServerSidePagination(prevState => ({ ...prevState, [key]: val }))
  }

  useEffect(() => setClientData(data), [data])

  const getRegistryFirmsByPagination = (sectorCodes, pageIndex, pageSize, searchKeyword = '', importerCountries = [], exporterCountries = []) => {
    apiController && apiController.abort()
    apiController = new AbortController()

    const hasValidKeyword = searchKeyword && searchKeyword.length >= 3
    const hasInvalidKeyword = searchKeyword && searchKeyword.length > 0 && searchKeyword.length < 3
    const shouldFetch = (sectorCodes.length > 0 || hasValidKeyword) && !hasInvalidKeyword

    if (shouldFetch) {
      updateUiState({ registryFirmsFetching: true })

      const params = {
        b2bCampaignId: queryParams.b2bCampaignId,
        page: pageIndex,
        pageSize,
        sectorCategory: activeFirmKey
      }

      if (sectorCodes.length > 0) {
        params.sectorCodes = sectorCodes.join(',')
      }

      if (hasValidKeyword) {
        params.searchKeyword = searchKeyword
      }

      if (importerCountries.length > 0) {
        params.firmImporterCountryCodes = importerCountries.map(c => c.countryCode).join(',')
      }

      if (exporterCountries.length > 0) {
        params.firmExporterCountryCodes = exporterCountries.map(c => c.countryCode).join(',')
      }

      getRegistryFirms(apiController, params).then((res) => {
        if (res.status === 200) {
          setFilteredRegistryFirmsDataByKey(activeFirmKey, res.data)
        } else {
          toast.error(t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })
        }
        updateUiState({ registryFirmsFetching: false })
      }).catch((err) => {
        if (!err?.config?.signal?.aborted) {
          updateUiState({ registryFirmsFetching: false })
          toast.error(t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })
        }
      })
    } else {
      if (!hasInvalidKeyword) {
        setFilteredRegistryFirmsDataByKey(activeFirmKey, { registryFirms: [], totalRecordCount: 0 })
      }
      updateUiState({ registryFirmsFetching: false })
    }
  }

  useEffect(() => {
    if (isServerSide) {
      getRegistryFirmsByPagination(
        filteredSectorsByKey.map((item) => item.code),
        serverSidePagination?.[activeFirmKey]?.pageIndex || 0,
        serverSidePagination?.[activeFirmKey]?.pageSize || uiState.pageSize,
        currentSearchKeyword,
        currentImporterCountries,
        currentExporterCountries
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serverSidePagination,
    filteredSectors,
    activeFirmKey,
    isServerSide,
    uiState.pageSize,
    uiState.searchKeywordByKey,
    uiState.importerCountriesByKey,
    uiState.exporterCountriesByKey
  ])

  const keyToFirmType = (key) => (key === 'spFirms'
    ? 'SP'
    : key === 'rfFirms'
      ? 'RF'
      : '')

  const fetchFirmsPaginate = async (pageIndex, pageSize) => {
    const firmType = keyToFirmType(activeFirmKey)
    if (!firmType) return

    try {
      updateUiState({ clientFirmsFetching: true })

      const res = await B2BAPI.get('/firms/paginate', {
        params: {
          b2bCampaignId: queryParams.b2bCampaignId,
          firmType,
          page: pageIndex,
          pageSize,
          _: Date.now()
        },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })

      const json = res.data

      setClientData(prev => {
        const next = { ...(prev || {}) }
        if (json?.spFirms) next.spFirms = json.spFirms
        if (json?.rfFirms) next.rfFirms = json.rfFirms
        if (json?.tradeRegistrySectors) next.tradeRegistrySectors = json.tradeRegistrySectors
        return next
      })
    } catch (e) {
      if (e?.code !== 'ERR_CANCELED') {
        toast.error(
          t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'),
          { position: 'top-right', autoClose: true, hideProgressBar: true }
        )
      }
    } finally {
      updateUiState({ clientFirmsFetching: false })
    }
  }
  const handleClientPageChange = (page) => {
    fetchFirmsPaginate(page, uiState.pageSize)
  }

  const handleClientPageSizeChange = (newSize) => {
    updateUiState({ pageSize: newSize })
    fetchFirmsPaginate(0, newSize)
  }

  const { handleSelectAllToggle, handleSelectCurrentPageToggle } = useFirmSelectionActions()
  const { selectionMode, includedFirms } = useFirmSelectionState(activeFirmKey)
  const getCurrentPageStatus = useFirmSelectionStore((state) => state.getCurrentPageSelectionStatus)

  const currentPageData = isServerSide
    ? (filteredRegistryFirmsData?.[activeFirmKey]?.registryFirms || [])
    : (clientData?.[activeFirmKey]?.firms || [])

  const { isAllSelected: isCurrentPageAllSelected, isPartiallySelected: isCurrentPagePartiallySelected } =
    getCurrentPageStatus(activeFirmKey, currentPageData)

  const handleSelectAllToggleWrapper = (checked) => {
    handleSelectAllToggle(activeFirmKey, checked)
  }

  const handleSelectCurrentPageToggleWrapper = (checked) => {
    handleSelectCurrentPageToggle(activeFirmKey, checked, currentPageData)
  }

  const isLoading = uiState.registryFirmsFetching || fetching || uiState.clientFirmsFetching
  const hasNoData = currentPageData.length === 0
  const shouldDisableCheckboxes = uiConfig.disableInteraction || isLoading || hasNoData

  const renderSelectionControls = () => (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
      <Tooltip title={t('mailTemplate.wizard.firmList.selectAllTooltip')} arrow>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px', padding: '4px 8px', cursor: shouldDisableCheckboxes ? 'not-allowed' : 'pointer', opacity: shouldDisableCheckboxes ? 0.5 : 1 }}
          onClick={() => {
            if (!shouldDisableCheckboxes) {
              handleSelectAllToggleWrapper(selectionMode !== 'all')
            }
          }}
        >
          <Checkbox
            checked={selectionMode === 'all'}
            indeterminate={selectionMode === 'exclude' ||
              (selectionMode === 'include' && includedFirms.length > 0)}
            onChange={(e) => handleSelectAllToggleWrapper(e.target.checked)}
            disabled={shouldDisableCheckboxes}
            size="small"
            sx={{ padding: '4px' }}
            onClick={(e) => e.stopPropagation()}
          />
          <Typography variant="body2" sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}>
            {t('mailTemplate.wizard.firmList.all')}
          </Typography>
        </div>
      </Tooltip>
      <Tooltip title={t('mailTemplate.wizard.firmList.selectPageTooltip')} arrow>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px', padding: '4px 8px', cursor: shouldDisableCheckboxes ? 'not-allowed' : 'pointer', opacity: shouldDisableCheckboxes ? 0.5 : 1 }}
          onClick={() => {
            if (!shouldDisableCheckboxes) {
              handleSelectCurrentPageToggleWrapper(!isCurrentPageAllSelected)
            }
          }}
        >
          <Checkbox
            checked={isCurrentPageAllSelected}
            indeterminate={isCurrentPagePartiallySelected}
            onChange={(e) => handleSelectCurrentPageToggleWrapper(e.target.checked)}
            disabled={shouldDisableCheckboxes}
            size="small"
            sx={{ padding: '4px' }}
            onClick={(e) => e.stopPropagation()}
          />
          <Typography variant="body2" sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}>
            {t('mailTemplate.wizard.firmList.page')}
          </Typography>
        </div>
      </Tooltip>
    </div>
  )

  return (
    <>
      <div className="flex flex-wrap justify-between items-center p-2 gap-2">
        <div className="flex items-center gap-2 grow" style={{
          justifyContent: isBelowSmScreen
            ? 'space-between'
            : 'start'
        }}>
          {!uiConfig.hideActionButtons && (
            <div className="flex flex-col">
              {/* <IconButton
                onClick={() => updateUiState(
                  { additionalEmailsModalOpen: true })}
                color="primary"
              >
                <PlaylistAdd/>
              </IconButton> */}
              <UploadExcelFirmListModalBtn
                excelFirms={excelFirms}
                setExcelFirms={setExcelFirms}
              />
            </div>
          )}

          {onRefresh && (
            <IconButton
              onClick={onRefresh}
              color="primary"
              disabled={uiState.registryFirmsFetching || fetching ||
                uiState.clientFirmsFetching}
            >
              {fetching ? <CircularProgress size={24}/> : <Refresh/>}
            </IconButton>
          )}

          {!uiConfig.hideTabs && (
            <ButtonGroup
              size={isBelowMdScreen ? 'small' : 'medium'}
              orientation={isBelowSmScreen ? 'vertical' : 'horizontal'}
              fullWidth={isBelowSmScreen}
              variant="outlined"
              aria-label="Basic button group"
              className="dir-rtl justify-start"
              style={{ maxWidth: '100%' }}
              disabled={uiState.registryFirmsFetching ||
                uiState.clientFirmsFetching}
            >
              {firmKeys?.map((item, index) => {
                const localizationPath = `mailTemplate.wizard.firmList.tables.${item}`
                const localizedText = t(localizationPath)
                const btnText = localizationPath !== localizedText
                  ? localizedText
                  : item
                return (
                  <Button
                    variant={activeFirmKey === item ? 'contained' : 'outlined'}
                    key={index}
                    onClick={() => setActiveFirmKey(item)}
                    sx={{ borderRadius: 2 }}
                  >
                    {btnText}
                  </Button>
                )
              })}
            </ButtonGroup>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center p-2 gap-2 flex-wrap">
        <div className="flex gap-2 items-center">
          {renderSelectionControls()}
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          {isServerSide && (activeFirmKey !== 'rfFirms' && activeFirmKey !== 'spFirms') && (
            <>
              <div style={{
                minWidth: 200,
                maxWidth: 300,
                flexShrink: 0
              }}>
                <AutoCompleteChip
                  size="small"
                  value={currentImporterCountries}
                  onChange={(_, val) => {
                    updateUiState({
                      importerCountriesByKey: {
                        ...uiState.importerCountriesByKey,
                        [activeFirmKey]: val || []
                      }
                    })
                    setServerSidePaginationByKey(activeFirmKey, {
                      pageSize: serverSidePagination?.[activeFirmKey]?.pageSize || 10,
                      pageIndex: 0
                    })
                  }}
                  options={countryData || []}
                  getOptionLabel={(opt) => opt?.countryName || ''}
                  isOptionEqualToValue={(option, value) => option?.countryCode === value?.countryCode}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.countryCode}>
                      {opt.countryName}
                    </li>
                  )}
                  chipLabelKey="countryCode"
                  disabled={uiState.registryFirmsFetching || !filteredSectorsByKey?.length}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('mailTemplate.wizard.firmList.filterImportCountries')}
                      placeholder={t('mailTemplate.wizard.firmList.search')}
                    />
                  )}
                />
              </div>

              <div style={{
                minWidth: 200,
                maxWidth: 300,
                flexShrink: 0
              }}>
                <AutoCompleteChip
                  size="small"
                  value={currentExporterCountries}
                  onChange={(_, val) => {
                    updateUiState({
                      exporterCountriesByKey: {
                        ...uiState.exporterCountriesByKey,
                        [activeFirmKey]: val || []
                      }
                    })
                    setServerSidePaginationByKey(activeFirmKey, {
                      pageSize: serverSidePagination?.[activeFirmKey]?.pageSize || 10,
                      pageIndex: 0
                    })
                  }}
                  options={countryData || []}
                  getOptionLabel={(opt) => opt?.countryName || ''}
                  isOptionEqualToValue={(option, value) => option?.countryCode === value?.countryCode}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt.countryCode}>
                      {opt.countryName}
                    </li>
                  )}
                  chipLabelKey="countryCode"
                  disabled={uiState.registryFirmsFetching || !filteredSectorsByKey?.length}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('mailTemplate.wizard.firmList.filterExportCountries')}
                      placeholder={t('mailTemplate.wizard.firmList.search')}
                    />
                  )}
                />
              </div>
            </>
          )}

          {isServerSide && (
            <div style={{
              minWidth: 200,
              maxWidth: 300,
              flexShrink: 0
            }}>
              <DebouncedInput
                fullWidth
                size="small"
                variant="outlined"
                value={currentSearchKeyword}
                debounce={800}
                onChange={(value) => {
                  const strValue = String(value)

                  if (strValue.length === 0 || strValue.length >= 3) {
                    updateUiState({
                      searchKeywordByKey: {
                        ...uiState.searchKeywordByKey,
                        [activeFirmKey]: strValue
                      }
                    })
                    setServerSidePaginationByKey(activeFirmKey, {
                      pageSize: serverSidePagination?.[activeFirmKey]?.pageSize || 10,
                      pageIndex: 0
                    })
                  } else {
                    updateUiState({
                      searchKeywordByKey: {
                        ...uiState.searchKeywordByKey,
                        [activeFirmKey]: strValue
                      }
                    })
                  }
                }}
                label={t('mailTemplate.wizard.firmList.searchCompanyOrProfile')}
                className="is-full sm:is-auto"
                disabled={uiState.registryFirmsFetching || !filteredSectorsByKey?.length}
              />
            </div>
          )}

          <div style={{
            minWidth: 200,
            maxWidth: 300,
            flexShrink: 0
          }}>
            {isServerSide
              ? (
                <AutoCompleteChip
                  size="small"
                  value={filteredSectorsByKey}
                  onChange={(_, val) => {
                    setFilteredSectorsByKey(val)
                    setSelectedTradeRegistrySectors(val)
                    setServerSidePaginationByKey(activeFirmKey, {
                      pageSize: serverSidePagination?.[activeFirmKey]?.pageSize || 10,
                      pageIndex: 0
                    })
                  }}
                  options={tradeRegistrySectors || []}
                  getOptionLabel={opt =>
                    `${opt.code}${opt?.name?.[languageGSD] ? ` - ${opt.name[languageGSD]}` : ''}`
                  }
                  renderOption={(props, opt) => {
                    const label = `${opt.code}${opt?.name?.[languageGSD] ? ` - ${opt.name[languageGSD]}` : ''}`
                    const relevant = !!opt.relevant
                    return (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', fontWeight: relevant ? 700 : 400 }}>
                          <span>{label}</span>
                          {relevant && (
                            <Tooltip title={t('mailTemplate.campaignReview.relevant')} placement="right">
                              <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ ml: 'auto' }}/>
                            </Tooltip>
                          )}
                        </Box>
                      </li>
                    )
                  }}
                  chipLabelKey="code"
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('mailTemplate.wizard.firmList.sector')}
                      placeholder={t('mailTemplate.wizard.firmList.search')}
                      error={!filteredSectorsByKey?.length}
                    />
                  )}
                />
                )
              : (
                <DebouncedInput
                  fullWidth
                  value={uiState.globalFilter ?? ''}
                  onChange={(value) => updateUiState(
                    { globalFilter: String(value) })}
                  placeholder={t('mailTemplate.wizard.firmList.search')}
                  className="is-full sm:is-auto"
                />
                )}
          </div>

          <CustomTextField
            select
            value={isServerSide
              ? (serverSidePagination?.[activeFirmKey]?.pageSize || 10)
              : uiState.pageSize.toString()}
            onChange={(e) => {
              const val = Number(e.target.value)
              if (isServerSide) {
                setServerSidePaginationByKey(activeFirmKey,
                  { pageIndex: 0, pageSize: val })
              } else {
                handleClientPageSizeChange(val)
              }
            }}
            className="is-[70px]"
            disabled={uiState.registryFirmsFetching ||
              uiState.clientFirmsFetching}
          >
            <MenuItem value="10">{McPageHelpers.numToLocaleString(
              10)}</MenuItem>
            <MenuItem value="25">{McPageHelpers.numToLocaleString(
              25)}</MenuItem>
            <MenuItem value="50">{McPageHelpers.numToLocaleString(
              50)}</MenuItem>
          </CustomTextField>
        </div>
      </div>

      {isServerSide
        ? (
          <FirmListServerSide
            firmKey={activeFirmKey}
            languageGSD={languageGSD}
            serverSidePagination={serverSidePagination[activeFirmKey]}
            setSelectedFirms={setSelectedFirms}
            data={filteredRegistryFirmsData?.[activeFirmKey]?.registryFirms ||
              []}
            rowCount={filteredRegistryFirmsData?.[activeFirmKey]?.totalRecordCount ||
              0}
            fetching={uiState.registryFirmsFetching || fetching}
            setServerSidePaginationByKey={(val) => setServerSidePaginationByKey(
              activeFirmKey, val)}
            disableInteraction={uiConfig.disableInteraction}
          />
          )
        : (
          <FirmListSemiClientSide
            firmKey={activeFirmKey}
            data={clientData?.[activeFirmKey] ||
              { firms: [], page: 0, pageCount: 0 }}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            setSelectedFirms={setSelectedFirms}
            fetching={fetching || uiState.clientFirmsFetching}
            fuzzyFilter={fuzzyFilter}
            pageSize={uiState.pageSize}
            globalFilter={uiState.globalFilter}
            setGlobalFilter={(value) => updateUiState({ globalFilter: value })}
            disableInteraction={uiConfig.disableInteraction}
            hideWebsiteColumn={uiConfig.hideWebsiteColumn}
            onPageChange={handleClientPageChange}
            onPageSizeChange={handleClientPageSizeChange}
          />
          )}

      <AdditionalEmailModal
        open={uiState.additionalEmailsModalOpen}
        setOpen={(open) => updateUiState({ additionalEmailsModalOpen: open })}
        additionalEmails={additionalEmails}
        setAdditionalEmails={setAdditionalEmails}
      />
    </>
  )
}

export default FirmListManager
